import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faDownload,
  faSearch,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

const ReferencesDetailModal = ({
  show,
  onClose,
  title,
  description,
  references,
  kpiType,
  formatDate,
  getStatusColorClass,
  getTransportIcon,
  onDetailsClick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "header.reference",
    direction: "asc",
  });
  const [filteredReferences, setFilteredReferences] = useState([]);

  // Filter and sort references
  useEffect(() => {
    if (!references) {
      setFilteredReferences([]);
      return;
    }

    let filtered = [...references];

    // Apply search filtering
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          (item.header.reference &&
            item.header.reference.toLowerCase().includes(lowerSearchTerm)) ||
          (item.header.customer_name &&
            item.header.customer_name
              .toLowerCase()
              .includes(lowerSearchTerm)) ||
          (item.header.trafficStatus &&
            item.header.trafficStatus.toLowerCase().includes(lowerSearchTerm))
        );
      });
    }

    // Sort the filtered data
    filtered.sort((a, b) => {
      const getNestedValue = (obj, path) => {
        if (!obj || !path) return null;
        const keys = path.split(".");
        return keys.reduce(
          (o, key) => (o && o[key] !== undefined ? o[key] : null),
          obj
        );
      };

      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // Handle date comparison
      if (sortConfig.key.includes("date") && aValue && bValue) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric comparison
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    });

    setFilteredReferences(filtered);
  }, [references, searchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleExport = () => {
    if (!filteredReferences || filteredReferences.length === 0) return;

    // Create CSV data
    const headers = [
      "Reference",
      "Customer",
      "Status",
      "Entry Point",
      "ETA Date",
      "Alta Date",
      "Operation Type",
      "Crossing Date",
      "Pedimento Paid",
    ];

    const csvData = filteredReferences.map((item) => {
      return [
        item.header.reference || "N/A",
        item.header.customer_name || "N/A",
        item.header.trafficStatus || "N/A",
        item.header.pedimento?.pedimento_entry_point || "N/A",
        formatDate(item.header.ebookingDates?.fechaETA),
        formatDate(item.header.ebookingDates?.fechaAlta),
        item.header.operation_type === "1"
          ? "Import"
          : item.header.operation_type === "2"
          ? "Export"
          : item.header.operation_type || "N/A",
        formatDate(item.header.detail?.dates_data?.date_crossing),
        formatDate(item.header.detail?.dates_data?.date_pedimento_paid),
      ]
        .map((value) => `"${value}"`)
        .join(",");
    });

    const csv = [headers.join(","), ...csvData].join("\n");

    // Create a blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${title.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getKpiIcon = () => {
    switch (kpiType) {
      case "outOfTime":
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-red-500"
          />
        );
      case "validationProcess":
        return (
          <FontAwesomeIcon icon={faHourglassHalf} className="text-yellow-500" />
        );
      case "etaCompliance":
        return (
          <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />
        );
      case "processingTime":
        return (
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
        );
      default:
        return (
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
        );
    }
  };

  const getAnalysisText = () => {
    switch (kpiType) {
      case "outOfTime":
        return `These references have exceeded the 3-day validation process window without an "Alert" status. They require immediate attention as they are now outside the standard processing timeframe. The validation process should typically be completed within 3 days from the Alta date.`;
      case "validationProcess":
        return `These references are currently in the validation process phase. They are within the 3-day window from the Alta date and do not have an "Alert" status in their timeline. They are proceeding according to the normal workflow.`;
      case "etaCompliance":
        return `These references have an ETA date that is more than 7 days from the Alta date, which does not meet the compliance target. The KPI goal is to keep this difference to 7 days or less to ensure timely processing and to avoid delays in the logistics chain.`;
      case "processingTime":
        return `This data shows the processing times for references, including the "Tiempo de Salida" (ETA to Crossing) and the time between Arrival and Pedimento Payment. Analyzing these timeframes helps identify bottlenecks and opportunities for optimization in the processing workflow.`;
      default:
        return `Detailed analysis of the selected references. The analysis provides insights into the current state and performance metrics related to your operations.`;
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return (
        <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-400 text-xs" />
      );
    return sortConfig.direction === "asc" ? (
      <FontAwesomeIcon icon={faSortUp} className="ml-1 text-blue-500 text-xs" />
    ) : (
      <FontAwesomeIcon
        icon={faSortDown}
        className="ml-1 text-blue-500 text-xs"
      />
    );
  };

  if (!show) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center overflow-y-auto"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="bg-white rounded-lg shadow-xl mx-auto max-w-5xl w-full max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
            <div className="flex items-center">
              {getKpiIcon()}
              <h2 className="text-xl font-semibold ml-2">{title}</h2>
              <span className="ml-4 bg-white text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {filteredReferences.length} References
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>

          {/* Description and Analysis */}
          <div className="bg-blue-50 p-4 border-b border-blue-100">
            <div className="flex items-start">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-blue-500 mt-1 mr-3"
              />
              <div>
                <h3 className="font-medium text-lg text-blue-800 mb-1">
                  Analysis
                </h3>
                <p className="text-blue-700 text-sm">{getAnalysisText()}</p>
                {description && (
                  <p className="text-blue-600 text-sm mt-2">{description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Search and Export Controls */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-3 items-center">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search references..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 px-4 py-2 rounded-md font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} />
              <span>Export to CSV</span>
            </button>
          </div>

          {/* References Table */}
          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("header.reference")}
                  >
                    <div className="flex items-center">
                      Reference {renderSortIcon("header.reference")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("header.customer_name")}
                  >
                    <div className="flex items-center">
                      Customer {renderSortIcon("header.customer_name")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("header.trafficStatus")}
                  >
                    <div className="flex items-center">
                      Status {renderSortIcon("header.trafficStatus")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("header.ebookingDates.fechaAlta")}
                  >
                    <div className="flex items-center">
                      Alta Date{" "}
                      {renderSortIcon("header.ebookingDates.fechaAlta")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("header.ebookingDates.fechaETA")}
                  >
                    <div className="flex items-center">
                      ETA Date {renderSortIcon("header.ebookingDates.fechaETA")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReferences.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No references found matching your criteria
                    </td>
                  </tr>
                ) : (
                  filteredReferences.map((reference) => (
                    <tr
                      key={reference.header.reference}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {reference.header.reference}
                        </div>
                        <div className="text-xs text-gray-500">
                          {reference.header.pedimento?.pedimento_number ||
                            "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {reference.header.customer_name || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {reference.header.customer_division || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {reference.header.trafficStatus ? (
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColorClass(
                              reference.header.trafficStatus
                            )}`}
                          >
                            {reference.header.trafficStatus}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(reference.header.ebookingDates?.fechaAlta)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(reference.header.ebookingDates?.fechaETA)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => onDetailsClick(reference)}
                          className="text-blue-600 hover:text-blue-800 transition-colors font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between items-center border-t border-gray-200 rounded-b-lg">
            <div className="text-sm text-gray-500">
              Showing {filteredReferences.length} of {references?.length || 0}{" "}
              references
            </div>
            <button
              onClick={onClose}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferencesDetailModal;
